// The step-action schemas: the only definition of step data; every type is `z.infer` (docs/step-action/model.md).
import { z } from 'zod'
import { LOCATOR } from '../targeting/locator.ts'
import { parseDefinitionId, parseSurfaceId } from '../stable-surfaces/id.ts'
import { ROUTE_PATTERN } from '../polish/sightmap/route.ts'
import { TEXT, VAR_NAME } from './text.ts'

// A Sightmap component address. `params` values identify the instance; `selector` is a literal selector inside the
// addressed component (an owner-scoped binding).
export const COMPONENT_REF = z.object({
  surfaceId: z.string().min(1).transform(parseSurfaceId),
  path: z.array(z.object({ componentId: z.string().min(1).transform(parseDefinitionId), params: z.record(TEXT).optional() }).strict()).min(1),
  selector: z.string().min(1).optional(),
}).strict()

// Which element a slot addresses: a Sightmap component, or a locator for an element with no Sightmap entry.
export const TARGET_REF = z.union([z.object({ component: COMPONENT_REF }).strict(), z.object({ locator: LOCATOR }).strict()])

// Each arm carries its own slots. Base fields (`target`, `checks`, `condition`, …) join with their schemas.

export const TEXT_ENTRY_STEP_ACTION = z.object({
  type: z.enum(['type', 'input']),
  action: z.string(),
  input: TEXT,
  append: z.boolean().optional(),
  store: VAR_NAME.optional(),
}).strict()

// Playwright's held-key names; `ControlOrMeta` is Meta on macOS, else Control.
export const MODIFIER = z.enum(['Alt', 'Control', 'ControlOrMeta', 'Meta', 'Shift'])

// Playwright's multi-character key names: its US layout codes and their aliases (`keyboard.press`).
export const NAMED_KEY = z.enum([
  ...MODIFIER.options, 'AltGraph', 'AltLeft', 'AltRight', 'ControlLeft', 'ControlRight', 'MetaLeft', 'MetaRight', 'ShiftLeft', 'ShiftRight',
  'Escape', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
  'Backquote', 'Digit0', 'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Minus', 'Equal', 'Backslash', 'Backspace',
  'Tab', 'KeyA', 'KeyB', 'KeyC', 'KeyD', 'KeyE', 'KeyF', 'KeyG', 'KeyH', 'KeyI', 'KeyJ', 'KeyK', 'KeyL', 'KeyM',
  'KeyN', 'KeyO', 'KeyP', 'KeyQ', 'KeyR', 'KeyS', 'KeyT', 'KeyU', 'KeyV', 'KeyW', 'KeyX', 'KeyY', 'KeyZ',
  'BracketLeft', 'BracketRight', 'CapsLock', 'Semicolon', 'Quote', 'Enter', 'Comma', 'Period', 'Slash', 'Space', 'ContextMenu',
  'PrintScreen', 'ScrollLock', 'Pause', 'PageUp', 'PageDown', 'Insert', 'Delete', 'Home', 'End', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown',
  'AudioVolumeMute', 'AudioVolumeDown', 'AudioVolumeUp', 'MediaTrackNext', 'MediaTrackPrevious', 'MediaPlayPause',
  'NumLock', 'NumpadDivide', 'NumpadMultiply', 'NumpadSubtract', 'NumpadAdd', 'NumpadDecimal', 'NumpadEnter',
  'Numpad0', 'Numpad1', 'Numpad2', 'Numpad3', 'Numpad4', 'Numpad5', 'Numpad6', 'Numpad7', 'Numpad8', 'Numpad9',
])

// A character key: one printable US-layout character, the key that types it (`a`, `A`, `@`).
export const KEY_CHAR = z.string().regex(/^[!-~]$/)

// One key pressed with its held modifiers. Wire and stored form: chord text `Control+a` (key-chord.ts).
export const KEY_CHORD = z.object({
  modifiers: z.array(MODIFIER),
  key: z.union([NAMED_KEY, z.object({ char: KEY_CHAR }).strict()]),
}).strict()

export const KEY_EVENT = z.enum(['press', 'down', 'up'])

export const PRESS_KEY_STEP_ACTION = z.object({
  type: z.literal('pressKey'),
  action: z.string(),
  keys: z.array(KEY_CHORD).min(1),
  event: KEY_EVENT.optional(),
}).strict()

// The option is matched against each option's label and value (execute/element/dispatch/select.ts).
export const SELECT_STEP_ACTION = z.object({
  type: z.literal('select'),
  action: z.string(),
  option: TEXT,
}).strict()

// Binds to a test-file pool slug.
export const FILE_SLUG = TEXT.min(1)

// No file: the pool's default pick.
export const FILES_STEP_ACTION = z.object({
  type: z.enum(['uploadFile', 'dropFile']),
  action: z.string(),
  files: z.array(FILE_SLUG),
}).strict()

export const DRAG_DIRECTION = z.enum(['up', 'down', 'left', 'right'])

// `direction` nudges the item one slot. `preview` stores the live order of the `list` items before the release.
export const DRAG_STEP_ACTION = z.object({
  type: z.enum(['drag', 'dragTo']),
  action: z.string(),
  dest: z.union([z.object({ target: TARGET_REF }).strict(), z.object({ direction: DRAG_DIRECTION }).strict()]),
  preview: z.object({ list: TARGET_REF, store: VAR_NAME }).strict().optional(),
}).strict()

// An absolute URL, or a path under the current page's origin: decided on the bound string (execute/navigation.ts).
export const NAVIGATE_STEP_ACTION = z.object({
  type: z.literal('navigate'),
  action: z.string(),
  url: TEXT,
}).strict()

// What a value capture stores: an element's value (`attribute`, else its input value, href or text), the clipboard,
// an OPFS file (no `path`: every file's path and size), or a page-level value (a generator part: fresh test data).
export const READ_VARIABLE_STEP_ACTION = z.object({
  type: z.literal('read_variable'),
  action: z.string(),
  store: VAR_NAME,
  from: z.union([
    z.object({ element: TARGET_REF, attribute: z.string().min(1).optional() }).strict(),
    z.object({ clipboard: z.literal(true) }).strict(),
    z.object({ opfs: z.object({ path: TEXT.optional() }).strict() }).strict(),
    z.object({ value: TEXT }).strict(),
  ]),
}).strict()

// Text an assertion reads: it holds `value` (`contains`) or is `value` (`equals`).
export const TEXT_MATCH = z.object({ op: z.enum(['contains', 'equals']), value: TEXT }).strict()

// Which route: a pattern; `params` binds its named params (each matches only its bound value).
export const ROUTE_REF = z.object({ pattern: ROUTE_PATTERN, params: z.record(TEXT).optional() }).strict()

// Checks: one assertion each. `description` is prose. `property` names a Sightmap property of the target, read in
// place of its own text or value.
const PRESENCE_CHECK = z.object({ assertion: z.enum(['exists', 'not_exists', 'visible', 'in_view']), description: z.string(), target: TARGET_REF }).strict()
const CONTAINS_TEXT_CHECK = z.object({ assertion: z.literal('contains_text'), description: z.string(), target: TARGET_REF, property: z.string().min(1).optional(), match: TEXT_MATCH }).strict()
const HAS_VALUE_CHECK = z.object({ assertion: z.literal('has_value'), description: z.string(), target: TARGET_REF, property: z.string().min(1).optional(), value: TEXT }).strict()
const HAS_COUNT_CHECK = z.object({ assertion: z.literal('has_count'), description: z.string(), target: TARGET_REF, count: z.number().int().nonnegative() }).strict()
// `state`: a semantic or enablement state, or a state marker the observation renders (sightmap-checks.ts).
const HAS_STATE_CHECK = z.object({ assertion: z.literal('has_state'), description: z.string(), target: TARGET_REF, state: z.string().min(1) }).strict()

export const ELEMENT_CHECK = z.discriminatedUnion('assertion', [PRESENCE_CHECK, CONTAINS_TEXT_CHECK, HAS_VALUE_CHECK, HAS_COUNT_CHECK, HAS_STATE_CHECK])

export function isElementAssertion(assertion: string): assertion is ElementCheck['assertion'] {
  return ELEMENT_CHECK.optionsMap.has(assertion)
}

// `url_contains` `contains`: the URL holds the text; `route`: its path is on the route. `abstract` and `reachSurfaceCheck` are judged by the runner;
// `note`, a legacy string check, is displayed and never evaluated.
export const STEP_CHECK = z.discriminatedUnion('assertion', [
  ...ELEMENT_CHECK.options,
  z.object({ assertion: z.literal('url_contains'), description: z.string(),
    url: z.union([z.object({ contains: TEXT }).strict(), z.object({ route: ROUTE_REF }).strict()]) }).strict(),
  z.object({ assertion: z.literal('abstract'), description: z.string() }).strict(),
  z.object({ assertion: z.literal('reachSurfaceCheck'), description: z.string(), surfaceId: z.string().min(1).transform(parseSurfaceId) }).strict(),
  z.object({ assertion: z.literal('note'), description: z.string() }).strict(),
])

// A condition whose target leaves are `T`. The Sightmap check resolver binds a stored target until it reads
// `ComponentRef`, so the runner evaluates `Condition<LegacyTarget>` (legacy-check.ts).
export type Condition<T> =
  | { type: 'route'; operator: 'matches' | 'not_matches'; route: RouteRef }
  | { type: 'target'; operator: 'exists' | 'not_exists'; target: T }
  | { type: 'all' | 'any'; conditions: Condition<T>[] }
  | { type: 'not'; condition: Condition<T> }

export type StepCondition = Condition<TargetRef>

// A step guard: the step runs iff it holds.
export const STEP_CONDITION: z.ZodType<StepCondition, z.ZodTypeDef, unknown> = z.lazy(() => z.union([
  z.object({ type: z.literal('route'), operator: z.enum(['matches', 'not_matches']), route: ROUTE_REF }).strict(),
  z.object({ type: z.literal('target'), operator: z.enum(['exists', 'not_exists']), target: TARGET_REF }).strict(),
  z.object({ type: z.enum(['all', 'any']), conditions: z.array(STEP_CONDITION).min(1) }).strict(),
  z.object({ type: z.literal('not'), condition: STEP_CONDITION }).strict(),
]))

export type ComponentRef = z.infer<typeof COMPONENT_REF>
export type TargetRef = z.infer<typeof TARGET_REF>
export type TextEntryStepAction = z.infer<typeof TEXT_ENTRY_STEP_ACTION>
export type Modifier = z.infer<typeof MODIFIER>
export type KeyChord = z.infer<typeof KEY_CHORD>
export type KeyEvent = z.infer<typeof KEY_EVENT>
export type PressKeyStepAction = z.infer<typeof PRESS_KEY_STEP_ACTION>
export type SelectStepAction = z.infer<typeof SELECT_STEP_ACTION>
export type FilesStepAction = z.infer<typeof FILES_STEP_ACTION>
export type DragStepAction = z.infer<typeof DRAG_STEP_ACTION>
export type NavigateStepAction = z.infer<typeof NAVIGATE_STEP_ACTION>
export type ReadVariableStepAction = z.infer<typeof READ_VARIABLE_STEP_ACTION>
export type TextMatch = z.infer<typeof TEXT_MATCH>
export type RouteRef = z.infer<typeof ROUTE_REF>
export type ElementCheck = z.infer<typeof ELEMENT_CHECK>
export type StepCheck = z.infer<typeof STEP_CHECK>
